<template>
  <div
    class="avatar-container d-flex justify-center align-center"
    :class="`avatar-container--${size} avatar-container--${theme}`"
  >
    <div class="avatar-container__text">{{ getInitials(name) }}</div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useUserProfileStore } from "@/stores/userProfileStore";

const userProfileStore = useUserProfileStore();

withDefaults(
  defineProps<{
    name: string;
    size?: "small" | "medium" | "large";
    theme?: "theme1" | "theme2";
  }>(),
  {
    size: "medium",
    theme: "theme1",
  }
);

function getInitials(text: string): string {
  const words = text.split(" ");
  if (words.length > 1) {
    const firstLetter =
      words[0].length > 0 ? words[0][0].trim().toUpperCase() : "";
    const secondLetter =
      words[1].length > 1 ? words[1][0].trim().toUpperCase() : "";
    const initials = firstLetter + secondLetter;
    return initials.length > 0 ? initials : "-";
  } else if (words.length === 1 && words[0].length > 0) {
    const initial = words[0][0].trim().toUpperCase();
    return initial.length > 0 ? initial : "-";
  } else {
    return "-";
  }
}

const colors = [
  "--dusty-rose",
  "--violet",
  "--green",
  "--red-orange",
  "--dark-yellow",
  "--blue",
];

const userColor = computed(() =>
  userProfileStore.trustGroup.company.companyName.length == 0
    ? "var(--light-grey-1)"
    : `var(${userNameToColor(userProfileStore.trustGroup.company.companyName)}`
);

function userNameToColor(userName: string) {
  var sum = 0;
  for (let i = 0; i < userName.length; i++) {
    sum += userName.charCodeAt(i);
  }
  const index = sum % colors.length;

  return colors[index];
}
</script>

<style scoped lang="scss">
.avatar-container {
  border-radius: 50%;
  cursor: pointer;

  &--theme1 {
    background-color: v-bind(userColor);

    .avatar-container__text {
      color: var(--white);
    }
  }

  &--theme2 {
    background-color: var(--white);
    border: 1px solid var(--grey);

    .avatar-container__text {
      color: var(--black);
    }
  }

  .avatar-container--theme1 {
    background-color: var(--digital-rose);

    .avatar-container__text {
      color: var(--white);
    }
  }

  &--small {
    width: 18px;
    height: 18px;

    .avatar-container__text {
      font-size: 10px;
      line-height: 10px;
    }
  }

  &--medium {
    width: 28px;
    height: 28px;

    .avatar-container__text {
      font-size: 14px;
      line-height: 14px;
    }
  }

  &--large {
    width: 32px;
    height: 32px;

    .avatar-container__text {
      font-size: 16px;
      line-height: 16px;
    }
  }
}
</style>
